import React, { useEffect, useState, useCallback } from 'react';
import { RequireRole } from '../../components/CallcenterV2/RequireRole';
import {
  fetchWorkSchedules, createWorkSchedule, updateWorkSchedule, deleteWorkSchedule,
  fetchIntervals, addInterval, removeInterval,
  fetchOverrides, addOverride, removeOverride,
} from '../../services/callcenterV2/workSchedulesService';
import '../../components/CallcenterV2/ccv2-components.css';
import './config-styles.css';

const DAYS_PT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function WeekGrid({ intervals = [] }) {
  const hours = [6, 8, 10, 12, 14, 16, 18, 20, 22];

  const isActive = (day, hour) =>
    intervals.some((iv) => {
      if (iv.day_of_week !== day) return false;
      const [sh, sm] = (iv.start_time || '00:00').split(':').map(Number);
      const [eh, em] = (iv.end_time || '23:59').split(':').map(Number);
      const start = sh * 60 + sm;
      const end = eh * 60 + em;
      const cell = hour * 60;
      return cell >= start && cell < end;
    });

  return (
    <div style={{ overflowX: 'auto', marginTop: '0.75rem' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: '0.7rem', minWidth: 400 }}>
        <thead>
          <tr>
            <th style={{ padding: '4px 6px', color: '#475569' }}>Hora</th>
            {DAYS_PT.map((d) => <th key={d} style={{ padding: '4px 6px', color: '#475569', fontWeight: 700 }}>{d}</th>)}
          </tr>
        </thead>
        <tbody>
          {hours.map((h) => (
            <tr key={h}>
              <td style={{ padding: '3px 6px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{String(h).padStart(2, '0')}:00</td>
              {[0, 1, 2, 3, 4, 5, 6].map((day) => (
                <td key={day} style={{
                  padding: '3px 6px',
                  textAlign: 'center',
                  background: isActive(day, h) ? 'rgba(74,123,167,0.25)' : '#f8fafc',
                  borderRadius: 3,
                  width: 32,
                }}>
                  {isActive(day, h) ? '●' : ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IntervalForm({ scheduleId, onSaved }) {
  const [form, setForm] = useState({ dayOfWeek: 1, startTime: '08:00', endTime: '18:00' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await addInterval(scheduleId, form);
      onSaved();
    } catch (e) {
      alert(`Erro: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end', marginTop: '0.75rem' }}>
      <div>
        <label className="ccv2-p5-label">Dia</label>
        <select className="ccv2-p5-select" value={form.dayOfWeek} onChange={(e) => setForm((p) => ({ ...p, dayOfWeek: parseInt(e.target.value, 10) }))}>
          {DAYS_PT.map((d, i) => <option key={i} value={i}>{d}</option>)}
        </select>
      </div>
      <div>
        <label className="ccv2-p5-label">Início</label>
        <input type="time" className="ccv2-p5-input" value={form.startTime} onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))} />
      </div>
      <div>
        <label className="ccv2-p5-label">Fim</label>
        <input type="time" className="ccv2-p5-input" value={form.endTime} onChange={(e) => setForm((p) => ({ ...p, endTime: e.target.value }))} />
      </div>
      <button className="ccv2-p5-btn ccv2-p5-btn-primary" disabled={saving} onClick={save}>
        {saving ? '⌛' : '+'} Adicionar
      </button>
    </div>
  );
}

function ScheduleDetail({ schedule, onClose }) {
  const [intervals, setIntervals] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(true);

  const reload = useCallback(async () => {
    setLoadingDetail(true);
    try {
      const [ivRes, ovRes] = await Promise.allSettled([
        fetchIntervals(schedule.id_work_schedule),
        fetchOverrides(schedule.id_work_schedule),
      ]);
      if (ivRes.status === 'fulfilled') setIntervals(Array.isArray(ivRes.value) ? ivRes.value : (ivRes.value?.intervals || []));
      if (ovRes.status === 'fulfilled') setOverrides(Array.isArray(ovRes.value) ? ovRes.value : (ovRes.value?.overrides || []));
    } catch (_e) {}
    finally { setLoadingDetail(false); }
  }, [schedule.id_work_schedule]);

  useEffect(() => { reload(); }, [reload]);

  const handleRemoveInterval = async (ivId) => {
    await removeInterval(schedule.id_work_schedule, ivId);
    reload();
  };

  return (
    <div className="ccv2-p5-modal-overlay" onClick={onClose}>
      <div className="ccv2-p5-modal" style={{ maxWidth: 680 }} onClick={(e) => e.stopPropagation()}>
        <div className="ccv2-p5-modal-header">
          <h3 className="ccv2-p5-modal-title">Escala: {schedule.name}</h3>
          <button className="ccv2-p5-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="ccv2-p5-modal-body">
          {loadingDetail ? <div className="ccv2-config-loading"><div className="ccv2-spinner" /></div> : (
            <>
              <div className="ccv2-p5-section-title">🗓️ Preview Semanal</div>
              <WeekGrid intervals={intervals} />

              <div className="ccv2-p5-section-title" style={{ marginTop: '1rem' }}>Intervalos ({intervals.length})</div>
              {intervals.map((iv) => (
                <div key={iv.id_work_schedule_interval} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.875rem' }}>
                  <span>{DAYS_PT[iv.day_of_week]} · {iv.start_time} – {iv.end_time}</span>
                  <button className="ccv2-p5-btn ccv2-p5-btn-danger" style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem' }}
                    onClick={() => handleRemoveInterval(iv.id_work_schedule_interval)}>🗑️</button>
                </div>
              ))}
              <IntervalForm scheduleId={schedule.id_work_schedule} onSaved={reload} />
            </>
          )}
        </div>
        <div className="ccv2-p5-modal-footer">
          <button className="ccv2-p5-btn ccv2-p5-btn-secondary" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

function WorkSchedules() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editSchedule, setEditSchedule] = useState(null);
  const [detailSchedule, setDetailSchedule] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', timezone: 'America/Sao_Paulo', isActive: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchWorkSchedules(true);
      setSchedules(Array.isArray(data) ? data : (data?.schedules || []));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ name: '', description: '', timezone: 'America/Sao_Paulo', isActive: true });
    setEditSchedule(null);
    setShowForm(true);
  };

  const openEdit = (s) => {
    setForm({ name: s.name, description: s.description || '', timezone: s.timezone || 'America/Sao_Paulo', isActive: s.is_active !== false });
    setEditSchedule(s);
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editSchedule) {
        await updateWorkSchedule(editSchedule.id_work_schedule, form);
        setSuccess('Escala atualizada.');
      } else {
        await createWorkSchedule(form);
        setSuccess('Escala criada.');
      }
      setShowForm(false);
      setEditSchedule(null);
      load();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deleteWorkSchedule(deleteConfirm.id_work_schedule);
      setSuccess('Escala removida.');
      setDeleteConfirm(null);
      load();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <RequireRole roles={['manager', 'admin', 'root']}>
      <div className="ccv2-page-container ccv2-p5-container">
        <div className="ccv2-page-header">
          <div className="ccv2-page-title-area">
            <h1>Escalas de Trabalho</h1>
            <p>Configure horários e feriados dos agentes</p>
          </div>
          <div className="ccv2-page-actions">
            <button className="ccv2-p5-btn ccv2-p5-btn-secondary" onClick={load}>🔄</button>
            <button className="ccv2-p5-btn ccv2-p5-btn-primary" onClick={openCreate}>+ Nova Escala</button>
          </div>
        </div>

        {success && <div className="ccv2-p5-success">{success}</div>}
        {error && <div className="ccv2-p5-error">⚠️ {error} <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}>×</button></div>}

        {loading ? <div className="ccv2-config-loading"><div className="ccv2-spinner" /></div> : (
          <div className="ccv2-config-card">
            <div className="ccv2-config-table-wrapper">
              <table className="ccv2-config-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Fuso Horário</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.length === 0 ? (
                    <tr><td colSpan={4} className="ccv2-p5-empty">Nenhuma escala configurada.</td></tr>
                  ) : schedules.map((s) => (
                    <tr key={s.id_work_schedule}>
                      <td className="ccv2-cell-name">{s.name}</td>
                      <td style={{ fontSize: '0.8rem', color: '#64748b' }}>{s.timezone || 'UTC'}</td>
                      <td><span className={`ccv2-p5-badge ${s.is_active !== false ? 'ccv2-p5-badge-active' : 'ccv2-p5-badge-inactive'}`}>{s.is_active !== false ? 'Ativo' : 'Inativo'}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.4rem' }}>
                          <button className="ccv2-p5-btn ccv2-p5-btn-primary" style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                            onClick={() => setDetailSchedule(s)} title="Ver detalhes e intervalos">🗓️ Detalhar</button>
                          <button className="ccv2-p5-btn ccv2-p5-btn-warning" style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                            onClick={() => openEdit(s)}>✏️</button>
                          <button className="ccv2-p5-btn ccv2-p5-btn-danger" style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                            onClick={() => setDeleteConfirm(s)}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Create/Edit form modal */}
        {showForm && (
          <div className="ccv2-p5-modal-overlay" onClick={() => setShowForm(false)}>
            <div className="ccv2-p5-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ccv2-p5-modal-header">
                <h3 className="ccv2-p5-modal-title">{editSchedule ? 'Editar Escala' : 'Nova Escala'}</h3>
                <button className="ccv2-p5-modal-close" onClick={() => setShowForm(false)}>×</button>
              </div>
              <div className="ccv2-p5-modal-body">
                <div className="ccv2-p5-field">
                  <label className="ccv2-p5-label">Nome *</label>
                  <input className="ccv2-p5-input" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} style={{ width: '100%' }} />
                </div>
                <div className="ccv2-p5-field">
                  <label className="ccv2-p5-label">Descrição</label>
                  <input className="ccv2-p5-input" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} style={{ width: '100%' }} />
                </div>
                <div className="ccv2-p5-field">
                  <label className="ccv2-p5-label">Fuso Horário</label>
                  <select className="ccv2-p5-select" value={form.timezone} onChange={(e) => setForm((p) => ({ ...p, timezone: e.target.value }))} style={{ width: '100%' }}>
                    <option value="America/Sao_Paulo">America/Sao_Paulo (BRT)</option>
                    <option value="America/Manaus">America/Manaus (AMT)</option>
                    <option value="America/Belem">America/Belem (BRT)</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />
                  <span className="ccv2-p5-label" style={{ margin: 0 }}>Ativo</span>
                </label>
              </div>
              <div className="ccv2-p5-modal-footer">
                <button className="ccv2-p5-btn ccv2-p5-btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
                <button className="ccv2-p5-btn ccv2-p5-btn-primary" disabled={!form.name.trim() || saving} onClick={handleSave}>
                  {saving ? '⌛' : '💾'} {editSchedule ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirm */}
        {deleteConfirm && (
          <div className="ccv2-p5-modal-overlay" onClick={() => setDeleteConfirm(null)}>
            <div className="ccv2-p5-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ccv2-p5-modal-header" style={{ background: 'linear-gradient(135deg,#e74c3c,#c0392b)', borderRadius: '14px 14px 0 0' }}>
                <h3 className="ccv2-p5-modal-title" style={{ color: '#fff' }}>Confirmar Exclusão</h3>
                <button className="ccv2-p5-modal-close" style={{ color: '#fff' }} onClick={() => setDeleteConfirm(null)}>×</button>
              </div>
              <div className="ccv2-p5-modal-body">
                <p>Excluir a escala <strong>{deleteConfirm.name}</strong>? Isso removerá todos os intervalos e overrides vinculados.</p>
              </div>
              <div className="ccv2-p5-modal-footer">
                <button className="ccv2-p5-btn ccv2-p5-btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancelar</button>
                <button className="ccv2-p5-btn ccv2-p5-btn-danger" disabled={saving} onClick={handleDelete}>
                  {saving ? '⌛' : '🗑️'} Excluir
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Detail modal */}
        {detailSchedule && (
          <ScheduleDetail schedule={detailSchedule} onClose={() => setDetailSchedule(null)} />
        )}
      </div>
    </RequireRole>
  );
}

export default WorkSchedules;
